// Cookie consent banner — DPDP Act 2023 + GDPR Article 7 require explicit
// consent before non-essential cookies (analytics, tracking) are set. We store
// the choice in localStorage so the banner doesn't reappear next visit.
//
// Behaviour:
//  - First visit → banner shown at bottom of screen.
//  - Accept → opt-in stored, banner hides, analytics tracker (utils/tracker)
//    is permitted to send page-visit pings.
//  - Decline → opt-out stored, banner hides; tracker silently no-ops (it reads
//    this flag at call-time).
//  - Either choice persists until the user clears site data.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "travelsate:cookie-consent";

export function getCookieConsent() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY); // "accepted" | "declined" | null
  } catch {
    return null;
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Render only when no prior choice exists; small delay so the banner
    // doesn't flash before page paint.
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const record = (choice) => {
    try { window.localStorage.setItem(STORAGE_KEY, choice); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-t border-[#D4AF37]/30 shadow-[0_-8px_24px_rgba(0,0,0,0.5)]"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="max-w-5xl mx-auto px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-white/80 text-xs leading-relaxed flex-1">
          We use strictly necessary cookies to keep you signed in and a single
          analytics cookie to understand how the site is used.{" "}
          <Link to="/privacy" className="text-[#D4AF37] underline" data-testid="cookie-banner-privacy-link">Learn more</Link>.
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => record("declined")}
            data-testid="cookie-decline-button"
            className="px-4 py-2 text-xs text-white/70 border border-white/15 rounded hover:bg-white/5 transition-colors"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => record("accepted")}
            data-testid="cookie-accept-button"
            className="px-4 py-2 text-xs font-medium bg-[#D4AF37] text-black rounded hover:bg-[#E5C468] transition-colors"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
